import { MutationObserver } from '@tanstack/react-query'
import React, { useContext } from 'react'

import type { ClientTracingInfo, OrusSession, SessionResult } from '@orus.eu/backend'
import { ensureError, TechnicalError } from '@orus.eu/error'
import { getDefaultLanguageFromUrl } from '@orus.eu/pharaoh'
import { isSuccess } from '@orus.eu/result'
import { orusOrganization } from '@orus.eu/right-access-management'
import { sleep } from '@orus.eu/sleep'

import { trpcLoader } from '../client.ts'
import { queryClient } from '../shared/query-client.ts'

import { safeDecodeBase64 } from './base64-utils.ts'
import {
  getEnhancedTrackingInfo,
  safeLocalStorageSetItem,
  trackGoogleAdsClick,
  validateGclidSource,
} from './google-ads-tracking.ts'
import { logger } from './logger.ts'
import { getPostHogInstance, onPostHogLoaded } from './posthog-integration.ts'
import {
  getPostHogAnonymousId,
  getPostHogDistinctId,
  getPostHogSessionId,
  identifyUserInPostHog,
  resetPostHogIdentity,
} from './posthog-utils.ts'

function getSessionUserId(sessionResult: SessionResult | undefined): string | null {
  if (!sessionResult || !isSuccess(sessionResult)) return null
  return sessionResult.output.user?.id ?? null
}

class SessionManager {
  private currentSessionResult?: SessionResult
  private sessionResultPromiseListeners: Array<(sessionResult: Promise<SessionResult>) => void> = []
  private sessionResultListeners: Array<(sessionResult: SessionResult) => void> = []
  private currentSessionResultPromise?: Promise<SessionResult>
  private hasIdentifiedPostHog = false
  private hasSentPostHogIdsAfterLoad = false

  /**
   * The first load fires /rest/ensure_orus_session. Deferred to the first real read rather than done in the
   * constructor: a module-level `new` would fire it on import — before a test's MSW server can listen, and
   * before any consumer asked for a session.
   */
  private ensureLoaded(): Promise<SessionResult> {
    const alreadyLoading = this.currentSessionResultPromise
    if (alreadyLoading) return alreadyLoading

    const initialLoad = this.loadSession()

    // Register the PostHog-loaded refresh AFTER the initial load completes.
    // This prevents a race where both requests arrive without a session cookie.
    void initialLoad.then(() => {
      onPostHogLoaded(() => {
        if (!this.hasSentPostHogIdsAfterLoad) {
          this.hasSentPostHogIdsAfterLoad = true
          void this.refreshSession()
        }
      })
    })

    return initialLoad
  }

  private loadSession(): Promise<SessionResult> {
    const sessionResultPromise = loadSession()
    this.currentSessionResultPromise = sessionResultPromise

    // notify session loading listeners
    this.sessionResultPromiseListeners.forEach((listener) => listener(sessionResultPromise))

    // notify session listeners (in time)
    sessionResultPromise.then(
      (result) => {
        if (this.currentSessionResultPromise !== sessionResultPromise) return

        const previousSessionUserId = getSessionUserId(this.currentSessionResult)
        this.currentSessionResult = result
        if (previousSessionUserId !== getSessionUserId(result)) {
          void queryClient.resetQueries({ queryKey: trpcLoader.contracts.getHelpContactInformation.queryKey() })
        }

        if (isSuccess(result)) {
          const session = result.output
          if (!session.user) {
            /**
             * Clear SDK so the nextuser does not collide with previous Person
             * => Avoid Refused to merge already identified person" errors
             */
            if (this.hasIdentifiedPostHog) {
              resetPostHogIdentity()
            }
            // Allow identify again after logout / anonymous session
            this.hasIdentifiedPostHog = false
          } else {
            const identifyUser = () => {
              if (!session.user || this.hasIdentifiedPostHog) return
              this.hasIdentifiedPostHog = true

              const organization = getPostHogOrganizationTraits(session.permissions)

              if ('email' in session.user) {
                identifyUserInPostHog(
                  {
                    id: session.user.id,
                    email: session.user.email,
                    firstName: session.user.firstName ?? null,
                    lastName: session.user.lastName ?? null,
                    role:
                      session.permissions.type === 'partner'
                        ? (session.permissions.memberships[0]?.rolesPermissions[0]?.role ?? null)
                        : null,
                  },
                  organization,
                )
              } else if ('unverifiedEmail' in session.user) {
                identifyUserInPostHog(
                  {
                    id: session.user.id,
                    email: session.user.unverifiedEmail,
                    firstName: session.user.firstName ?? null,
                    lastName: session.user.lastName ?? null,
                  },
                  organization,
                )
              }
              // No refreshSession() here — the onPostHogLoaded refresh handles
              // sending PostHog IDs to the backend. If PostHog is already loaded,
              // that refresh fires right after this .then() callback and picks up
              // the post-identify distinct_id.
            }

            if (getPostHogInstance()) {
              identifyUser()
            } else {
              const maxRetries = 50 // 5 seconds max (50 * 100ms)
              let retryCount = 0
              const checkPostHog = () => {
                if (getPostHogInstance()) {
                  identifyUser()
                } else if (retryCount < maxRetries) {
                  retryCount++
                  setTimeout(checkPostHog, 100) // 100 ms
                }
                // If max retries reached, silently give up (PostHog may be blocked)
              }
              checkPostHog()
            }
          }
        }

        this.sessionResultListeners.forEach((listener) => listener(result))
      },
      (err: unknown) => {
        throw new TechnicalError(
          'The loadSession() call failed, which should not happen, because this function is meant to handle errors and retry',
          {
            cause: ensureError(err),
          },
        )
      },
    )

    return sessionResultPromise
  }

  getSessionResult(): Promise<SessionResult> {
    return this.ensureLoaded()
  }

  refreshSession(): Promise<SessionResult> {
    // A cold refresh *is* the initial load — going straight to `loadSession()` would skip the PostHog
    // registration `ensureLoaded` owns. Once loaded, reload unconditionally: that is what a refresh means.
    return this.currentSessionResultPromise ? this.loadSession() : this.ensureLoaded()
  }

  /**
   * Clears the server session and refreshes local session state. Meant for out-of-React callers (route
   * loaders/guards); inside React, useDisconnect wraps this to also navigate to the login page.
   */
  async logout(): Promise<void> {
    // Best-effort server logout: a failure (e.g. an already-invalid session for a deleted account) must
    // not abort local cleanup or the caller's redirect to login — the stale cookie is harmless once we
    // leave the app, and the next session load re-checks it anyway.
    await new MutationObserver(queryClient, trpcLoader.sessions.logout.mutationOptions())
      .mutate()
      .catch((error: unknown) => {
        logger.warning('Server logout failed; clearing local session anyway', {
          errStack: ensureError(error).stack ?? null,
        })
      })
    resetPostHogIdentity()
    await this.refreshSession()
  }

  onSessionRefreshing(listener: (sessionResultPromise: Promise<SessionResult>) => void): () => void {
    // Subscribing starts the load: `loadSession()` notifies these listeners as it begins, so a subscriber
    // that triggers nothing else would otherwise wait for a session nobody asked for.
    void this.ensureLoaded()
    this.sessionResultPromiseListeners.push(listener)
    return () => {
      this.sessionResultPromiseListeners = this.sessionResultPromiseListeners.filter((l) => l !== listener)
    }
  }

  onSessionRefreshed(listener: (sessionResult: SessionResult) => void): () => void {
    void this.ensureLoaded()
    this.sessionResultListeners.push(listener)

    // immediately notify them, so they can be informed if the session was loaded before they started listening
    if (this.currentSessionResult) listener(this.currentSessionResult)

    return () => {
      this.sessionResultListeners = this.sessionResultListeners.filter((l) => l !== listener)
    }
  }
}

const crossDomainTrackingTimeoutMs = 3000

// The background enrichment (and the session refresh it may trigger) must run at most once:
// refreshSession() calls loadSession() again, which would otherwise loop.
let hasRunBackgroundTrackingEnrichment = false

export const sessionManager: SessionManager = new SessionManager()

async function loadSession(): Promise<SessionResult> {
  // Fire /rest/ensure_orus_session immediately with the tracking info already available
  // synchronously (URL params, gclid cookie, localStorage). No tracking work is allowed on
  // the critical path: cross-domain tracking used to block session creation for up to 3s
  // (and once, forever, on a hanging /pm.html), stranding the user on a loading screen before
  // the funnel could load.
  const trackingInfo = getTrackingInfo(new URL(window.location.href).searchParams)
  const sessionResultPromise = getSessionWithRetries(trackingInfo)

  void enrichTrackingInBackground()

  return sessionResultPromise
}

/**
 * Best-effort tracking, off the critical path: persists the gclid cookie and pulls cross-domain
 * tracking data. If it surfaces a gclid we hadn't already sent with the initial session request,
 * it refreshes the session so the backend upserts the enriched tracing info (see
 * appendTracingUpdateIfNecessary / hasTracingInfoChanged on the backend).
 */
async function enrichTrackingInBackground(): Promise<void> {
  if (hasRunBackgroundTrackingEnrichment) return
  hasRunBackgroundTrackingEnrichment = true

  const gclidBeforeEnrichment = getEnhancedTrackingInfo(new URL(window.location.href).searchParams).gclid

  try {
    trackGoogleAdsClick()

    // Self-bounded by an internal safety timeout (finish() fires even if the iframe's `onload`
    // never does), so this always settles. Off the critical path, so a slow iframe delays nothing.
    await tryGetCrossDomainTrackingData()
  } catch (_error) {
    // Silently handle errors to avoid console warnings
  }

  const gclidAfterEnrichment = getEnhancedTrackingInfo(new URL(window.location.href).searchParams).gclid

  if (gclidAfterEnrichment && gclidAfterEnrichment !== gclidBeforeEnrichment) {
    void sessionManager.refreshSession()
  }
}

async function tryGetCrossDomainTrackingData(): Promise<void> {
  // If we're on app.orus.eu, try to get data from pm.html
  if (window.location.hostname === 'app.orus.eu') {
    try {
      // Load pm.html directly in an iframe. No reachability pre-fetch: the iframe loads it
      // anyway, and that extra fetch was the only hang vector the removed outer timeout guarded.
      const frame = document.createElement('iframe')
      frame.src = '/pm.html'
      frame.style.display = 'none'
      frame.style.visibility = 'hidden'

      await new Promise<void>((resolve) => {
        let settled = false
        const finish = () => {
          if (settled) return
          settled = true
          window.removeEventListener('message', messageHandler)
          if (frame.parentNode) {
            document.body.removeChild(frame)
          }
          resolve()
        }

        const messageHandler = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return

          if (event.data.type === 'getWebsiteData' && event.data.success) {
            const data = event.data.data
            if (data.gclid) {
              safeLocalStorageSetItem('gclid', data.gclid)
            }
            finish()
          }
        }

        frame.onload = () => {
          try {
            if (frame.contentWindow) {
              frame.contentWindow.postMessage(
                {
                  type: 'getWebsiteData',
                  requestId: Math.random().toString(36).substring(7),
                },
                window.location.origin,
              )

              window.addEventListener('message', messageHandler)
            } else {
              finish()
            }
          } catch (_error) {
            // Silently handle errors to avoid console warnings
            finish()
          }
        }

        // Safety timeout set up OUTSIDE of `onload`: guarantees the promise settles and the
        // hidden iframe is cleaned up even when the iframe never loads (onload never fires).
        setTimeout(finish, crossDomainTrackingTimeoutMs)

        document.body.appendChild(frame)
      })
    } catch (_error) {
      // Silently handle errors to avoid console warnings
    }
  }
}

async function getSessionWithRetries(trackingInfo: ClientTracingInfo): Promise<SessionResult> {
  while (true) {
    try {
      const response = await fetch('/rest/ensure_orus_session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'client-version': __CODE_VERSION_ID__,
        },
        body: JSON.stringify(trackingInfo),
      })
      if (response.ok) {
        return (await response.json()) as SessionResult
      }
    } catch (_err) {
      // nothing to do in case of error
    }
    // Whether the server is down, or we lost connection, let's wait
    // a bit and retry (forever, as there is no point in giving up)
    await sleep(1000)
  }
}

function getTrackingInfo(searchParams: URLSearchParams) {
  /**
   * The first url seen is available in the app through the "fus" query param
   * See sg.js file for more context
   */
  const firstUrlSeenEncoded = searchParams.get('fus')

  // Enhanced Google Ads tracking
  const enhancedTracking = getEnhancedTrackingInfo(searchParams)

  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      density: window.devicePixelRatio,
    },
    utm: {
      source: searchParams.get('utm_source') ?? searchParams.get('otm_source') ?? undefined,
      medium: searchParams.get('utm_medium') ?? searchParams.get('otm_medium') ?? undefined,
      campaign: searchParams.get('utm_campaign') ?? searchParams.get('otm_campaign') ?? undefined,
      term: searchParams.get('utm_term') ?? searchParams.get('otm_term') ?? undefined,
      content: searchParams.get('utm_content') ?? searchParams.get('otm_content') ?? undefined,
    },
    gclid: enhancedTracking.gclid,
    gclidSource: validateGclidSource(enhancedTracking.gclidSource),
    firstUrlSeen: firstUrlSeenEncoded ? safeDecodeBase64(firstUrlSeenEncoded) : undefined,
    app: { name: 'orus-web-app', version: __CODE_VERSION_ID__ },
    locale: navigator.languages[0],
    referrer: document.referrer,
    // Lets the backend skip the IP geolocation when the url already dictates the default language
    urlLanguage: getDefaultLanguageFromUrl(),
    // Early arming - capture PostHog IDs on every session heartbeat
    // This enables backend events to be enriched with the client's PostHog identity
    posthog_session_id: getPostHogSessionId() ?? undefined,
    posthog_distinct_id: getPostHogDistinctId() ?? undefined,
    posthog_anonymous_id: getPostHogAnonymousId() ?? undefined,
  }
}

function getPostHogOrganizationTraits(permissions: OrusSession['permissions']) {
  if (permissions.type === 'platform') return { technicalName: orusOrganization }
  if (permissions.type !== 'partner') return null

  const membership = permissions.memberships.at(0)
  if (!membership) return null

  const { technicalName, displayName, status, isManagedByOrus } = membership.organization

  return {
    technicalName,
    displayName,
    status,
    isManagedByOrus,
  }
}

export const SessionContext = React.createContext<OrusSession | null>(null)

export function useSession(): OrusSession {
  const session = useContext(SessionContext)
  if (!session) {
    throw new TechnicalError('The session context has not been provided')
  }
  return session
}
